import {
  faCircleExclamation,
  faFileInvoice,
  faFilterSlash
} from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import { AnimatePresence } from 'motion/react'
import { type ReactElement, useCallback } from 'react'
import { useOrderListWithFilters } from '~/api/queries/order'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { OrderCard } from '~/components/Scopes/Orders/OrderCard/OrderCard'
import { useInfiniteList } from '~/hooks/useInfiniteList'
import { resultsWrapper } from '~/pages/styles/Orders.css'
import { themeClass } from '~/styles/theme.css'
import { formatOrderTitle } from '../utils'

type OrdersListProps = {
  filters: { [key: string]: string | string[] }
  filtersLoading?: boolean
  quicksearch?: string
  onResetFilters?: () => void
  embedded?: boolean
}

export const OrdersList = ({
  filters,
  filtersLoading,
  quicksearch = '',
  onResetFilters,
  embedded = false
}: OrdersListProps): ReactElement => {
  const {
    orderlist,
    isOrderListLoading,
    isOrderListError,
    isFetchNextOrderListPageError,
    fetchNextOrderListPage,
    hasNextOrderListPage,
    isFetchingNextOrderListPage
  } = useOrderListWithFilters({
    filters,
    itemsPerPage: embedded ? 6 : 30,
    enabled: !filtersLoading
  })

  const endOfListReached = useCallback(() => {
    if (hasNextOrderListPage && !isFetchingNextOrderListPage) {
      fetchNextOrderListPage()
    }
  }, [
    hasNextOrderListPage,
    isFetchingNextOrderListPage,
    fetchNextOrderListPage
  ])

  const { lastItemRef } = useInfiniteList({
    onEndOfListReached: endOfListReached,
    enabled:
      !isOrderListLoading &&
      !isOrderListError &&
      !isFetchNextOrderListPageError &&
      !!hasNextOrderListPage
  })

  const filtredOrderlist = orderlist?.filter((order): boolean => {
    if (!quicksearch) return true
    return (
      order.company.name.toLowerCase().includes(quicksearch.toLowerCase()) ||
      formatOrderTitle(order).toLowerCase().includes(quicksearch.toLowerCase())
    )
  })

  const hasFilters =
    embedded === false &&
    Object.keys(filters).filter(
      (key) => key !== 'sort' && key !== 'sortDirection'
    ).length > 0

  const showEmptyState =
    !isOrderListLoading &&
    !isOrderListError &&
    !filtersLoading &&
    filtredOrderlist?.length === 0

  return (
    <div className={clsx(resultsWrapper, themeClass)}>
      <AnimatePresence>
        {isOrderListError && (
          <NoResult
            icon={faCircleExclamation}
            message="Une erreur est survenue lors de la récupération des commandes."
            isError
            isSmall={embedded}
          />
        )}
        {showEmptyState && hasFilters && (
          <NoResult
            icon={faFilterSlash}
            message="Aucune commande ne correspond à votre recherche."
            onDismiss={onResetFilters}
            dismissLabel="Réinitialiser les filtres"
          />
        )}
        {showEmptyState && !hasFilters && (
          <NoResult
            icon={faFileInvoice}
            message={
              embedded ? 'Aucune commande' : "Vous n'avez pas de commande."
            }
            isSmall={embedded}
          />
        )}
        {filtredOrderlist?.map((order) => (
          <OrderCard key={`order${order.id}`} order={order} />
        ))}
        {(isOrderListLoading ||
          isFetchingNextOrderListPage ||
          filtersLoading) &&
          [...Array(5)].map((_, i) => (
            <Shimmer
              width="100%"
              height={71}
              // biome-ignore lint/suspicious/noArrayIndexKey: Shimmer cant have unique key
              key={i + (orderlist?.length ?? 0)}
            />
          ))}
        {isFetchNextOrderListPageError && (
          <Alert
            variation={AlertVariation.Danger}
            message="Impossible de charger plus de commandes."
            onDismiss={false}
          />
        )}
        {hasNextOrderListPage && embedded && !isOrderListError && (
          <ButtonGroup align="center">
            <Button
              onClick={fetchNextOrderListPage}
              disabled={isFetchingNextOrderListPage}
              variation={ButtonVariation.Neutral}
              type="button"
              fullWidth
            >
              Charger plus de commandes
            </Button>
          </ButtonGroup>
        )}
      </AnimatePresence>
      {!embedded && <div ref={lastItemRef} style={{ height: '1px' }} />}
    </div>
  )
}
